/**
 * layoutConstants.ts - Centralized layout and dimension constants
 *
 * Purpose:
 *   Single source of truth for all layout-related constants used in AutomationFlows.
 *   Ensures consistency across components and reduces magic numbers.
 *
 * Usage:
 *   import { NODE_WIDTH, Z_INDEX, ANIMATION } from '../utils/layoutConstants';
 *
 * Categories:
 *   - Node dimensions: Physical size and spacing of flow nodes
 *   - Edge dimensions: Connection line styling
 *   - Spacing: Gaps and margins
 *   - Z-index layers: Proper element stacking
 *   - Animation timings: Consistent transitions
 *   - Breakpoints: Responsive design thresholds
 *
 * Related:
 *   - utils/layoutEngine.ts
 *   - nodes/stepNodeStyles.ts
 *   - edges/styles.ts
 */

/**
 * Node dimensions and spacing
 * Used by: layoutEngine, stepNodeStyles, node components
 */
export const NODE_WIDTH = 560;
export const NODE_MIN_HEIGHT = 100;
export const NODE_MAX_HEIGHT = 1000; // Increased to allow more content without scrollbar

// Step node height baselines.
// These values should stay in sync with step node styles so initial layout is stable
// (and we don't get a measure → relayout "jump" on insert).
export const STEP_NODE_COMPACT_HEIGHT = 100;
export const STEP_NODE_EXPANDED_MIN_HEIGHT = 200;

export const NODE_HORIZONTAL_SPACING = 180;
export const NODE_VERTICAL_SPACING = 120;

/**
 * Branch-specific spacing for switch nodes
 * Used by: convertTemplateToFlow, layoutEngine
 */
export const BRANCH_MARGIN = 80; // Gap between adjacent branch columns
export const BRANCH_VERTICAL_SPACING = 400; // Space between branches when spread vertically
export const BRANCH_VERTICAL_GAP = 88; // Gap between switch node bottom and first branch node top
export const INTRA_BRANCH_VERTICAL_GAP = 52; // Gap between consecutive nodes within a single branch lane

/**
 * Calculate horizontal spacing between branch lanes based on node width
 * This ensures branches are properly spaced relative to actual node dimensions
 */
export function calculateBranchSpacing(nodeWidth: number = NODE_WIDTH): number {
	return nodeWidth + BRANCH_MARGIN;
}

// Legacy constant for backward compatibility - use calculateBranchSpacing() instead
export const BRANCH_HORIZONTAL_SPACING = NODE_WIDTH + BRANCH_MARGIN;

/**
 * Flow spacing between sequential nodes
 * Used by: convertTemplateToFlow, layoutAdjustment
 */
export const FLOW_VERTICAL_SPACING = 200; // Space between sequential nodes in vertical layout
export const FLOW_HORIZONTAL_SPACING = 600; // Space between sequential nodes in horizontal layout - increased for better spacing

/**
 * Edge (connection line) dimensions
 * Used by: edge components, layoutEngine
 */
export const EDGE_STROKE_WIDTH = 2;
export const EDGE_LABEL_OFFSET = 20;

/**
 * Standard spacing values
 * Used by: various components for consistent gaps
 */
export const SPACING = {
	XS: 4,
	SM: 8,
	MD: 12,
	LG: 16,
	XL: 20,
	XXL: 24,
} as const;

/**
 * Z-index layers for proper stacking
 * Used by: All components that need layering control
 */
export const Z_INDEX = {
	NODE_BASE: 1,
	NODE_SELECTED: 5,
	BADGE: 10,
	CONTACT_INDICATOR: 10,
	EDGE_LABEL: 15,
	TOOLBAR: 100,
	DRAWER: 200,
	MODAL: 1000,
	TOAST: 2000,
} as const;

/**
 * Animation durations (milliseconds)
 * Used by: Transitions, animations, debouncing
 */
export const ANIMATION = {
	TRANSITION_DURATION: 300,
	TRANSITION_FAST: 150,
	TRANSITION_SLOW: 500,
	DEBOUNCE_DELAY: 150,
	CONTACT_ANIMATION_INTERVAL: 50,
	AUTOSAVE_DELAY: 1000,
} as const;

/**
 * Breakpoints for responsive design
 * Used by: Media queries, responsive layouts
 */
export const BREAKPOINTS = {
	SMALL: 640,
	MEDIUM: 1024,
	LARGE: 1280,
	XLARGE: 1536,
} as const;

/**
 * Trigger node specific dimensions
 * Used by: TriggerNode component
 */
export const TRIGGER_NODE = {
	WIDTH: NODE_WIDTH,
	HEIGHT: 100,
} as const;

/**
 * Handle (connection point) dimensions
 * Used by: Node handles for connecting edges
 */
export const HANDLE = {
	SIZE: 12,
	BORDER_WIDTH: 2,
} as const;

/**
 * Badge and indicator dimensions
 * Used by: Contact badges, case indicators, status badges
 */
export const BADGE = {
	SMALL: 8,
	MEDIUM: 12,
	LARGE: 16,
} as const;

/**
 * Border radius values for consistent rounding
 * Used by: Cards, buttons, badges
 */
export const BORDER_RADIUS = {
	SMALL: 4,
	MEDIUM: 6,
	LARGE: 8,
	FULL: 12,
	CIRCLE: '50%',
} as const;

/**
 * Common font sizes
 * Used by: Text elements across components
 */
export const FONT_SIZE = {
	XS: 10,
	SM: 11,
	BASE: 13,
	MD: 14,
	LG: 16,
	XL: 20,
	XXL: 26,
} as const;

/**
 * Segment node (conditional branch entry) specific dimensions
 * Used by: SegmentNode component
 *
 * MIN_HEIGHT accounts for expanded segment with full content:
 * - Header, tag sections (included/excluded/required), timing, instant options
 */
export const SEGMENT_NODE = {
	WIDTH: NODE_WIDTH, // All nodes use consistent 560px width
	MIN_HEIGHT: 360, // Reduced for more compact layout
	COLLAPSED_HEIGHT: 100,
	HEADER_HEIGHT: 48,
	TAG_SECTION_HEIGHT: 80,
} as const;

/**
 * Phantom "Add Path" edge dimensions
 * Used by: PhantomAddPathEdge component
 */
export const PHANTOM_EDGE = {
	BUTTON_SIZE: 48,
	OFFSET_FROM_DEFAULT: 100,
	LINE_COLOR: '#D1D5DB',
	LINE_DASH: '8,4',
} as const;
